import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { formatDateTime, money } from "../../lib/format";
import { AdminHeader, Badge, Row, Table } from "../ui";
import { EmptyState, PageLoader } from "../../components/States";

const STATUSES = ["all", "pending", "received", "paid", "shipped", "cancelled"];

export default function OrdersAdmin() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    adminApi.get("/admin/orders", { params: { status } })
      .then((r) => setRows(r.data.orders)).catch(() => setRows([]));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function setStatusFor(id, s) {
    await adminApi.put(`/admin/orders/${id}`, { status: s });
    toast.success(`Order marked ${s}`);
    load();
  }

  return (
    <div data-testid="admin-orders">
      <AdminHeader title="Orders" subtitle={rows ? `${rows.length} orders` : ""}>
        <div className="flex flex-wrap gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={status === s ? "btn-gold px-3 py-1.5 text-xs" : "btn-outline-nq px-3 py-1.5 text-xs"}
              data-testid={`order-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </AdminHeader>

      {!rows ? <PageLoader /> : rows.length === 0 ? (
        <EmptyState title="No orders yet" message="Orders placed through the shop will appear here." />
      ) : (
        <Table head={["Reference", "Customer", "Items", "Total", "Payment", "Status", "Date", ""]}>
          {rows.map((o) => (
            <Row key={o.id} testid="admin-order-row">
              <td className="px-4 py-3 font-mono text-xs gold-text">{o.reference}</td>
              <td className="px-4 py-3">
                <p className="font-medium">{o.customer?.name}</p>
                <p className="text-xs text-[color:var(--nq-stone)]">{o.customer?.email}</p>
              </td>
              <td className="px-4 py-3 text-xs">{(o.items || []).length}</td>
              <td className="px-4 py-3 font-mono text-xs">{money(o.total)}</td>
              <td className="px-4 py-3">
                <Badge tone={o.payment_status === "paid" ? "green" : o.payment_status === "failed" ? "red" : "neutral"}>
                  {o.payment_status}
                </Badge>
              </td>
              <td className="px-4 py-3"><Badge tone="gold">{o.status}</Badge></td>
              <td className="px-4 py-3 text-xs text-[color:var(--nq-stone)]">{formatDateTime(o.created_at)}</td>
              <td className="px-4 py-3">
                <select
                  value={o.status}
                  onChange={(e) => setStatusFor(o.id, e.target.value)}
                  className="rounded-lg border bg-transparent px-2 py-1 text-xs"
                  style={{ borderColor: "rgba(255,255,255,0.14)", background: "var(--nq-ink)" }}
                  data-testid="admin-order-status-select"
                >
                  {STATUSES.filter((s) => s !== "all").map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
            </Row>
          ))}
        </Table>
      )}
    </div>
  );
}
